import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Formula } from "building-types/options/formula";
import type { OptionChoices } from "building-types/options/optionChoices";
import type {
  DisconnectedReason,
  EntityHierarchies,
  EntityHierarchy,
  EntityParent,
  OptionSetEntityConnectedTable,
  OptionSetEntityDisconnectedTable,
  OptionSetEntityRelations,
  OptionSetEntityRelationship,
  OptionSetIdToEntityHierarchies,
} from "building-types/options/optionSetEntityRelationsTypes";
import { optionSetEntityRelationsDefault } from "building-types/options/optionSetEntityRelationsTypes";
import { groupBy } from "common/utils/arrays";
import type { Mutable } from "common/utils/types";
import { docEntityGetLevel } from "../../docGetLevels";
import { docGetActiveOptionTemplateOverrides } from "../../docGetOptionTemplates";
import { docEntityHandleHash } from "../../entity/core/handleHash";
import type { DocEntity } from "../../entity/docEntity";
import docEntityGetParent from "../../entity/docEntityGetParent";
import { docEntityHandleToEntityRef } from "../../entity/docEntityHandleToEntityRef";
import { docForEachFormulaDisabledEntity } from "../../entity/docForEachFormulaDisabledEntity";
import docGetEntity from "../../entity/docGetEntity";
import { docGetGroupImmediateChildren } from "../../entity/docGetGroupImmediateChildren";
import { docGetTableEntity } from "../../entity/docGetTableEntity";
import { formulaNew } from "../../formula/formulaNew";
import { formulaGetIdentifiers, isFormula } from "../../formula/formulaParsing";
import { isBooleanOptionSet, optionSetDefaultOptionId } from "../optionSetBaseUtils";
import { isExteriorPaletteOptionSet } from "../paletteOptionSet";
import { isElevationOptionSet, isMirrorOptionSet } from "../systemOptionSet";
import type { DisabledReason } from "./evaluateOptionDependencies";
import { evaluateOptionDependencies } from "./evaluateOptionDependencies";
import { evaluateOptionLocks, type LockedOption } from "./evaluateOptionLocks";

export function isStructuralOptionSet(relations: OptionSetEntityRelations, optionSetId: string) {
  const handles = relations.optionSetIdToAffectedEntities[optionSetId];

  return (
    isMirrorOptionSet(optionSetId) ||
    isElevationOptionSet(optionSetId) ||
    handles?.find((x) => x.type === "LEVEL" || x.type === "BLOCK" || x.type === "ROOM")
  );
}

function getNameAndConditionsFromEntity(
  doc: Doc,
  handle: DocEntityHandle,
  entityHash: string,
  groupFormula: Formula | null = null,
): { name: string; expr: string | null } {
  const entity = docGetEntity(doc, handle);
  if (entity) {
    const expr = entity["disabled"]?.formula ?? groupFormula?.formula ?? null;
    const name = entity["name"] && entity["name"] !== "" ? entity["name"] : entityHash;
    return {
      name,
      expr,
    };
  }
  return {
    name: entityHash,
    expr: null,
  };
}

function optionChoicesUsingDefaultValue(
  optionSetId: string,
  optionChoices: OptionChoices,
  optionSets: Doc["optionSets"],
) {
  const optionSet = optionSets?.[optionSetId];

  if (optionSet) {
    const defaultValue = optionSetDefaultOptionId(optionSet, optionSets);
    const currentValue = optionChoices?.[optionSetId]; // undefined is valid

    if (isBooleanOptionSet(optionSet)) {
      if (!(defaultValue === "true" || defaultValue === "false")) {
        console.assert(false, `'defaultValue' does not equal 'true' or 'false'. Actual value: ${defaultValue}`);
      }

      return currentValue === "true" ? defaultValue === "true" : defaultValue === "false";
    } else {
      if (!(defaultValue !== undefined)) {
        console.assert(false, "'defaultValue' is undefined");
      }

      return currentValue === undefined || currentValue === defaultValue;
    }
  }

  return true;
}

function getAncestorHierarchy(doc: Doc, childHandle: DocEntityHandle): EntityParent | null {
  // Ignore doc ref'd room layouts
  if (childHandle.type === "ROOM_LAYOUT" && !!docEntityHandleToEntityRef(doc, childHandle)) {
    return null;
  }

  const handle = docEntityGetParent(doc, childHandle);

  if (handle) {
    const { name, expr } = getNameAndConditionsFromEntity(doc, handle, docEntityHandleHash(handle));
    return {
      name,
      expr: expr ? formulaNew(expr) : null,
      handle,
      parent: getAncestorHierarchy(doc, handle),
    };
  }
  return null;
}

function getEntityHierarchies(optionSetId: string, optionSetIdToHierarchies: OptionSetIdToEntityHierarchies) {
  if (optionSetIdToHierarchies[optionSetId] === undefined) {
    optionSetIdToHierarchies[optionSetId] = {};
  }

  return optionSetIdToHierarchies[optionSetId];
}

function getEntityHierarchy(parent: EntityParent | null, hierarchies: EntityHierarchies) {
  const parentHash = parent ? docEntityHandleHash(parent.handle) : "Home";

  if (hierarchies[parentHash] === undefined) {
    hierarchies[parentHash] = { parent, children: {} };
  }

  return hierarchies[parentHash];
}

function createEntityChild(
  doc: Doc,
  handle: DocEntityHandle,
  groupFormula: Formula | null,
  hierarchy: EntityHierarchy,
) {
  const hash = docEntityHandleHash(handle);
  if (!(hash)) {
    console.assert(false);
  }

  const { name, expr } = getNameAndConditionsFromEntity(doc, handle, hash, groupFormula);
  if (!(hierarchy.children[hash] === undefined)) {
    console.assert(false, "duplicate hash encountered: " + hash);
  }

  hierarchy.children[hash] = {
    name,
    expr: expr ? formulaNew(expr) : null,
    handle,
    cannotEvalExpr: false,
    optionSetIsRedundant: false,
  };
}

function createConnectedRelationship(
  optionSetId: string,
  optionSets: Doc["optionSets"],
  hierarchies: OptionSetEntityRelationship["hierarchies"],
): OptionSetEntityRelationship {
  return {
    allOptionSets: optionSets,
    optionSetId,
    hierarchies,
  };
}

/**
 * Cached per doc identity. Relations are structural (disabled formulas + entity
 * ancestry) and do not depend on optionChoices, so the same Doc object can reuse
 * one result across preprocessing and generation.
 */
const optionSetEntityRelationsFromDocCache = new WeakMap<Doc, OptionSetEntityRelations>();

function optionSetEntityRelationsFromDocUncached(doc: Doc): OptionSetEntityRelations {
  const relations: OptionSetEntityRelations = optionSetEntityRelationsDefault();
  const ancestorHierarchyByHandleHash: Record<string, EntityParent | null> = {};

  function getCachedAncestorHierarchy(childHandle: DocEntityHandle): EntityParent | null {
    const handleHash = docEntityHandleHash(childHandle);
    if (handleHash in ancestorHierarchyByHandleHash) {
      return ancestorHierarchyByHandleHash[handleHash];
    }

    const hierarchy = getAncestorHierarchy(doc, childHandle);
    ancestorHierarchyByHandleHash[handleHash] = hierarchy;
    return hierarchy;
  }

  function callback(e: DocEntity, handle: DocEntityHandle, groupFormula: Formula | null = null) {
    switch (handle.type) {
      case "DRAWING_REF": {
        // ignore DRAWING_REFs
        break;
      }
      case "GROUP": {
        // process entities in group if group has a valid formula
        const group = docGetTableEntity(doc, handle);
        const groupFormula = group?.["disabled"];
        if (isFormula(groupFormula)) {
          for (const childHandle of docGetGroupImmediateChildren(doc, handle)) {
            const childEntity = docGetEntity(doc, childHandle);
            if (childEntity && childEntity["disabled"] === undefined) {
              callback(childEntity, childHandle, groupFormula); // recurse
            }
          }
        }
        break;
      }
      default: {
        const formula = e["disabled"] ?? groupFormula;
        if (isFormula(formula)) {
          const optionSetIds = formulaGetIdentifiers(formula);

          if (optionSetIds.length > 0) {
            const parent = getCachedAncestorHierarchy(handle);
            const level = docEntityGetLevel(doc, handle);

            for (const optionSetId of optionSetIds) {
              if (level && relations.optionSetIdToLevelId[optionSetId] !== null) {
                if (relations.optionSetIdToLevelId[optionSetId] === undefined) {
                  relations.optionSetIdToLevelId[optionSetId] = level.id;
                }
                if (relations.optionSetIdToLevelId[optionSetId] !== level.id) {
                  // 2 or more entities linked to same option set are not on the same level
                  // Set to null to avoid being reset to avoid setting again
                  relations.optionSetIdToLevelId[optionSetId] = null;
                }
              }
              // option set mapped to every entity that references its id in its conditions formula
              (relations.optionSetIdToAffectedEntities[optionSetId] ??= []).push(handle);

              // 1. optionSetId mapped to one or more entity hierarchies
              // 2. each hierarchy contains zero or more ancestor entities
              // 3. each hierarchy contains one or more child entities
              // 4. optionSetId is referenced in the disabled predicate of each child entity
              // 5. hierarchy is still valid if there are no ancestors in the hierarchy (just child entities)
              const hierarchies = getEntityHierarchies(optionSetId, relations.optionSetIdToEntityHierarchies);
              const hierarchy = getEntityHierarchy(parent, hierarchies);
              createEntityChild(doc, handle, groupFormula, hierarchy);
            }
          }
        }
        break;
      }
    }
  }

  docForEachFormulaDisabledEntity(doc, callback, true);

  return relations;
}

export function optionSetEntityRelationsFromDoc(doc: Doc): OptionSetEntityRelations {
  if (!doc) {
    return optionSetEntityRelationsDefault();
  }

  const cached = optionSetEntityRelationsFromDocCache.get(doc);
  if (cached) {
    return cached;
  }

  const relations = optionSetEntityRelationsFromDocUncached(doc);
  optionSetEntityRelationsFromDocCache.set(doc, relations);
  return relations;
}

export function evalOptionSetEntityRelations(
  bld: Pick<Building, "optionChoices" | "optionSetEntityRelations"> & {
    doc: Pick<Doc, "optionSets" | "optionLocks" | "optionTemplates" | "optionSetOverrides">;
  },
): {
  disconnected: OptionSetEntityDisconnectedTable;
  connected: OptionSetEntityConnectedTable;
  setToDefault: OptionChoices;
  lockedOptions: LockedOption[];
} {
  const disconnected: OptionSetEntityDisconnectedTable = {};
  const connected: OptionSetEntityConnectedTable = {};
  const setToDefault: Mutable<OptionChoices> = {};

  if (!bld?.doc?.optionSets) {
    return { disconnected, connected, setToDefault, lockedOptions: [] };
  }

  const { optionSets, optionLocks } = bld.doc;
  const { optionChoices, optionSetEntityRelations } = bld;

  // Get option template overrides (e.g., disconnect conditions defined in option templates)
  const optionTemplateOverrides = docGetActiveOptionTemplateOverrides(bld.doc);

  const optionChoicesWithDefaults: Mutable<OptionChoices> = {};
  for (const optionSetId in optionSets) {
    if (optionChoices?.[optionSetId] !== undefined) {
      optionChoicesWithDefaults[optionSetId] = optionChoices[optionSetId];
    } else {
      optionChoicesWithDefaults[optionSetId] = optionSetDefaultOptionId(optionSets[optionSetId], optionSets);
    }
  }

  const disabledResults = evaluateOptionDependencies(optionSetEntityRelations, optionSets, optionChoicesWithDefaults);
  const disabledForOptionSetId: Record<string, DisabledReason[]> = Object.fromEntries(
    Object.entries(groupBy(disabledResults, (r) => r.optionSetId)).map(([optionSetId, reasons]) => [
      optionSetId,
      reasons.map((r) => r.reason),
    ]),
  );

  const { lockedOptionSets, lockedOptions } = evaluateOptionLocks(
    optionSets,
    optionLocks,
    optionChoicesWithDefaults,
    optionTemplateOverrides ?? undefined,
  );
  const forceLockMap = Object.fromEntries(lockedOptionSets.map((l) => [l.optionSetId, l.relatedOptionSetIds]));

  for (const optionSetId of Object.keys(optionSets)) {
    const hierarchies = optionSetEntityRelations.optionSetIdToEntityHierarchies[optionSetId] ?? {};

    if (isExteriorPaletteOptionSet(optionSets[optionSetId])) {
      connected[optionSetId] = createConnectedRelationship(optionSetId, optionSets, hierarchies);
      continue;
    }

    // Elevation option sets carry pricing and identity, so they must never be marked disconnected
    // — being disconnected propagates to optionSet.disabled = true, which drops the elevation's
    // price from totals on plans that have a single elevation.
    if (isElevationOptionSet(optionSetId)) {
      connected[optionSetId] = createConnectedRelationship(optionSetId, optionSets, hierarchies);
      continue;
    }

    const disconnectIdentifiers = forceLockMap[optionSetId];
    // DisabledReason is a subset of DisconnectedReason
    const reasons: DisconnectedReason[] = [...(disabledForOptionSetId[optionSetId] ?? [])];

    if (disconnectIdentifiers) {
      reasons.push("FORCE_LOCK");

      if (!optionChoicesUsingDefaultValue(optionSetId, optionChoices, optionSets)) {
        setToDefault[optionSetId] = optionSetDefaultOptionId(optionSets[optionSetId], optionSets);
      }
    }

    if (reasons.length > 0) {
      disconnected[optionSetId] = {
        allOptionSets: optionSets,
        optionSetId,
        hierarchies,
        disconnectedReasons: reasons,
        ...(disconnectIdentifiers ? { disconnectIdentifiers } : {}),
      };
    } else {
      connected[optionSetId] = createConnectedRelationship(optionSetId, optionSets, hierarchies);
    }
  }

  return { disconnected, connected, setToDefault, lockedOptions };
}
